/**
 * Notices when a new version of Diil has been deployed under a page that is still open.
 *
 * The Toki/Hambi webviews keep a page alive for as long as the app is open — across
 * deploys. That page runs the old build, and the first time it needs a file the old build
 * had (a page's chunk or stylesheet) the file is gone: the screen stayed on its loading
 * state or lost its styles and buttons. Reloading fixes it, so this decides when to:
 *
 * - When the page becomes visible again, compare the main bundle it runs with the one the
 *   server now lists in /asset-manifest.json (at most every 5 minutes).
 * - On a mismatch, reload at the next in-app navigation — the visitor is leaving the
 *   screen anyway, so nothing typed is lost — or straight away if the page had been
 *   hidden for 30+ minutes (someone coming back to the app, not switching for a moment).
 *
 * Never more than one reload a minute, and never in development.
 */

const CHECK_EVERY_MS = 5 * 60 * 1000;
const LONG_AWAY_MS = 30 * 60 * 1000;
const RELOAD_KEY = 'diil_update_reload_at';

let updateReady = false;
let lastCheckAt = 0;
let hiddenAt = null;
let initialized = false;

/** The main bundle this page is running, e.g. /static/js/main.a505598e.js. */
const runningMain = () => {
  try {
    const script = document.querySelector('script[src*="/static/js/main."]');
    return script ? new URL(script.src, window.location.href).pathname : null;
  } catch (error) {
    return null;
  }
};

const checkForUpdate = async () => {
  if (updateReady || Date.now() - lastCheckAt < CHECK_EVERY_MS) return updateReady;
  lastCheckAt = Date.now();
  try {
    const response = await fetch('/asset-manifest.json', { cache: 'no-store' });
    if (!response.ok) return false;
    const manifest = await response.json();
    const latest = manifest && manifest.files && manifest.files['main.js'];
    const running = runningMain();
    if (latest && running && latest !== running) updateReady = true;
  } catch (error) {
    // offline or blocked — try again next time
  }
  return updateReady;
};

const reloadForUpdate = () => {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_KEY)) || 0;
    if (Date.now() - last < 60 * 1000) return;
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
  } catch (error) {
    // Without storage a reload loop could not be stopped — don't risk it
    return;
  }
  window.location.reload();
};

/** Call once at start-up. */
export function initVersionCheck() {
  if (initialized || process.env.NODE_ENV !== 'production' || typeof document === 'undefined') return;
  initialized = true;
  try {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        hiddenAt = Date.now();
        return;
      }
      const away = hiddenAt ? Date.now() - hiddenAt : 0;
      hiddenAt = null;
      checkForUpdate().then((ready) => {
        if (ready && away >= LONG_AWAY_MS) reloadForUpdate();
      });
    });
  } catch (error) {
    // ignore
  }
}

/** Call on every in-app navigation: the URL already shows the new page, so a reload lands there. */
export function reloadIfUpdateReady() {
  if (updateReady) reloadForUpdate();
}
